import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Building2, X, Copy, Check, MessageCircle } from 'lucide-react';
import './PayPalPayment.css';

interface Plan {
    name: string;
    sku: string;
    price: number;
    currency: string;
    displayCurrency: string;
    displayAmount: number;
}

interface SubscriptionRequestDTO {
    planType: 'FREE' | 'PREMIUM' | 'ELITE';
}

interface SubscriptionResponse {
    planType: 'FREE' | 'PREMIUM' | 'ELITE';
    status: string;
    trialStartDate?: string;
    trialEndDate?: string;
}

const PayPalPayment: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [planType, setPlanType] = useState<'FREE' | 'PREMIUM' | 'ELITE' | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [currentPlan, setCurrentPlan] = useState<SubscriptionResponse | null>(null);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [showBankDetails, setShowBankDetails] = useState(false);
    const [selectedPlanForBank, setSelectedPlanForBank] = useState<'FREE' | 'PREMIUM' | 'ELITE' | null>(null);
    const [copiedField, setCopiedField] = useState<string | null>(null);

    const navigate = useNavigate();

    const bankDetails = {
        companyName: 'SMARTLIFECHOICE LTD',
        accountType: 'Business',
        accountNumber: '20221216',
        sortCode: '23-05-80',
        swift: 'MYMBGB2L',
        iban: 'GB98MYMB 230580 20221216',
        whatsapp: '+447897239898'
    };

    useEffect(() => {
        const fetchCurrentPlan = async () => {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('Authentication token not found. Please log in.');
                navigate('/login');
                return;
            }

            try {
                const response = await axios.get('https://nrimarriage.in/api/v1/paypal/current-subscription', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setCurrentPlan(response.data);
                setPlanType(response.data.planType);
            } catch (err: any) {
                console.error('Failed to fetch current subscription:', err);
                if (err.response && err.response.status === 404) {
                    setCurrentPlan(null); // No active subscription
                } else {
                    setError('Failed to fetch current subscription.');
                }
            }
        };

        fetchCurrentPlan();
    }, [navigate]);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const token = localStorage.getItem('authToken');

                const response = await axios.get('https://nrimarriage.in/api/v1/paypal/plans',
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                setPlans(response.data);
            } catch (error) {
                console.error('Failed to fetch plans', error);
            }
        };
        fetchPlans();
    }, []);

    const handleSubscribe = async (selectedPlan: 'FREE' | 'PREMIUM' | 'ELITE') => {
        setLoading(true);
        setMessage(null);
        setError(null);

        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                setError('Authentication token not found. Please log in again.');
                setLoading(false);
                navigate('/login');
                return;
            }

            const response = await axios.post(
                'https://nrimarriage.in:/api/v1/paypal/create-order',
                { planType: selectedPlan } as SubscriptionRequestDTO,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const data = response.data;
            console.log('Create order response:', data);

            const formatDate = (dateStr: string) =>
                new Date(dateStr).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });

            const trialStart = data?.TrialStartDate ? formatDate(data.TrialStartDate) : null;
            const trialEnd = data?.TrialEndDate ? formatDate(data.TrialEndDate) : null;

            if (selectedPlan === 'FREE' && data?.message?.toLowerCase().includes('free trial activated')) {
                setMessage(`🎉 Free trial activated!\nValid from ${trialStart} to ${trialEnd}`);
                setTimeout(() => navigate('/home'), 2000);
            } else if (data?.ApprovalLink || data?.approvalLink) {
                const approvalLink = data.ApprovalLink ?? data.approvalLink;
                window.location.href = approvalLink;
            } else if (trialStart && trialEnd) {
                setMessage(`✅ Trial for ${selectedPlan} started!\nValid from ${trialStart} to ${trialEnd}`);
                setTimeout(() => navigate('/home'), 2000);
            } else {
                console.warn('Unexpected response:', data);
                setError('An unexpected response was received. Please contact support.');
            }
        } catch (err: any) {
            console.error('Payment error:', err);

            if (err.response) {
                const backendMsg = err.response.data?.message ?? 'Server returned an error.';
                setError(`Payment failed: ${backendMsg}`);
            } else {
                setError('Payment initiation failed. Please check your connection or try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBankTransfer = (selectedPlan: 'FREE' | 'PREMIUM' | 'ELITE') => {
        setSelectedPlanForBank(selectedPlan);
        setShowBankDetails(true);
    };

    const copyToClipboard = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedField(fieldName);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    const openWhatsApp = () => {
        const message = encodeURIComponent(`Hi, I've made a bank transfer for my ${selectedPlanForBank} subscription. Please find the proof of purchase attached.`);
        window.open(`https://wa.me/447897239898?text=${message}`, '_blank');
    };

    const BankDetailsModal = () => (
        <div className="bank-modal-overlay">
            <div className="bank-modal">
                <div className="bank-modal-header">
                    <h3>Bank Transfer Details</h3>
                    <button 
                        className="close-button5"
                        onClick={() => setShowBankDetails(false)}
                    >
                        <X size={24} />
                    </button>
                </div>
                
                <div className="bank-modal-content">
                    <div className="disclaimer">
                        <h4>⚠️ Important Disclaimer</h4>
                        <ul>
                            <li>Please include your email address as the payment reference</li>
                            <li>Bank transfers may take 1-3 business days to process</li>
                            <li>For international transfers, additional fees may apply from your bank</li>
                            <li>Send proof of payment through WhatsApp after completing the transfer</li>
                            <li>Your subscription will be activated once payment is confirmed</li>
                        </ul>
                    </div>

                    <div className="selected-plan-info">
                        <h4>Selected Plan: {selectedPlanForBank}</h4>
                        {plans.find(plan => plan.sku.split('_')[0] === selectedPlanForBank) && (
                            <p className="plan-amount">
                                Amount: {plans.find(plan => plan.sku.split('_')[0] === selectedPlanForBank)?.displayAmount.toLocaleString(undefined, {
                                    style: 'currency',
                                    currency: plans.find(plan => plan.sku.split('_')[0] === selectedPlanForBank)?.displayCurrency || 'GBP',
                                    minimumFractionDigits: 2,
                                })}
                            </p>
                        )}
                    </div>

                    <div className="bank-details-section">
                        <h4>Bank Transfer</h4>
                        <div className="bank-detail-item">
                            <label>Account Name:</label>
                            <div className="detail-value">
                                <span>{bankDetails.companyName}</span>
                                <button 
                                    className={`copy-btn ${copiedField === 'companyName' ? 'copied' : ''}`}
                                    onClick={() => copyToClipboard(bankDetails.companyName, 'companyName')}
                                >
                                    {copiedField === 'companyName' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="bank-detail-item">
                            <label>Account Type:</label>
                            <div className="detail-value">
                                <span>{bankDetails.accountType}</span>
                                <button 
                                    className={`copy-btn ${copiedField === 'accountType' ? 'copied' : ''}`}
                                    onClick={() => copyToClipboard(bankDetails.accountType, 'accountType')}
                                >
                                    {copiedField === 'accountType' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="bank-detail-item">
                            <label>Account Number:</label>
                            <div className="detail-value">
                                <span>{bankDetails.accountNumber}</span>
                                <button 
                                    className={`copy-btn ${copiedField === 'accountNumber' ? 'copied' : ''}`}
                                    onClick={() => copyToClipboard(bankDetails.accountNumber, 'accountNumber')}
                                >
                                    {copiedField === 'accountNumber' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="bank-detail-item">
                            <label>Sort Code:</label>
                            <div className="detail-value">
                                <span>{bankDetails.sortCode}</span>
                                <button 
                                    className={`copy-btn ${copiedField === 'sortCode' ? 'copied' : ''}`}
                                    onClick={() => copyToClipboard(bankDetails.sortCode, 'sortCode')}
                                >
                                    {copiedField === 'sortCode' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="bank-details-section international">
                        <h4>International Transfers</h4>
                        <div className="bank-detail-item">
                            <label>SWIFT/BIC:</label>
                            <div className="detail-value">
                                <span>{bankDetails.swift}</span>
                                <button 
                                    className={`copy-btn ${copiedField === 'swift' ? 'copied' : ''}`}
                                    onClick={() => copyToClipboard(bankDetails.swift, 'swift')}
                                >
                                    {copiedField === 'swift' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="bank-detail-item">
                            <label>IBAN:</label>
                            <div className="detail-value">
                                <span>{bankDetails.iban}</span>
                                <button 
                                    className={`copy-btn ${copiedField === 'iban' ? 'copied' : ''}`}
                                    onClick={() => copyToClipboard(bankDetails.iban, 'iban')}
                                >
                                    {copiedField === 'iban' ? <Check size={16} /> : <Copy size={16} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="whatsapp-section">
                        <h4>Send Proof of Purchase</h4>
                        <div className="whatsapp-contact">
                            <div className="whatsapp-info">
                                <MessageCircle size={20} />
                                <div>
                                    <p className="whatsapp-label">WhatsApp Number:</p>
                                    <p className="whatsapp-number">{bankDetails.whatsapp}</p>
                                </div>
                            </div>
                            <button 
                                className="whatsapp-btn"
                                onClick={openWhatsApp}
                            >
                                <MessageCircle size={18} />
                                Send via WhatsApp
                            </button>
                        </div>
                        <p className="whatsapp-note">
                            After completing your bank transfer, please send us the proof of purchase via WhatsApp for faster processing of your subscription activation.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="payment-container">
            <h2 className="title">Choose Your Subscription Plan</h2>

            {currentPlan && currentPlan.planType ? (
                <div className="current-plan">
                    <h3>Your Current Plan: {currentPlan.planType}</h3>
                    {currentPlan.trialStartDate && currentPlan.trialEndDate && (
                        <p>
                            Trial valid from{' '}
                            {new Date(currentPlan.trialStartDate).toLocaleDateString()} to{' '}
                            {new Date(currentPlan.trialEndDate).toLocaleDateString()}
                        </p>
                    )}
                    <p>Status: {currentPlan.status}</p>
                </div>
            ) : (
                <p>No active subscription found.</p>
            )}

            <div className="plans-grid">
                {plans.length === 0 ? (
                    <p>Loading plans...</p>
                ) : (
                    plans.map((plan) => {
                        const type = plan.sku.split('_')[0] as 'FREE' | 'PREMIUM' | 'ELITE';

                        return (
                            <div
                                key={plan.sku}
                                className={`plan-card ${planType === type ? 'selected' : ''}`}
                                onClick={() => setPlanType(type)}
                            >
                                <h3 className="plan-title">{plan.name}</h3>
                                <p className="plan-price">
                                    {plan.displayAmount.toLocaleString(undefined, {
                                        style: 'currency',
                                        currency: plan.displayCurrency,
                                        minimumFractionDigits: 2,
                                    })}
                                    {(type === 'PREMIUM' || type === 'ELITE') && '/month'}
                                </p>

                                <div className="payment-methods">
                                    <button
                                        className="subscribe-btn paypal-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubscribe(type);
                                        }}
                                        disabled={loading}
                                    >
                                        {loading && planType === type ? 'Processing...' : type === 'FREE' ? 'Start Free Trial' : 'Pay with PayPal'}
                                    </button>
                                    
                                    <button
                                        className="subscribe-btn bank-transfer-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBankTransfer(type);
                                        }}
                                    >
                                        <Building2 size={18} />
                                        Secure Bank Transfer
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {message && <p className="success-message">{message}</p>}
            {error && <p className="error-message">{error}</p>}

            {showBankDetails && <BankDetailsModal />}
        </div>
    );
};

export default PayPalPayment;